import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Enhance real photographs; the server-rendered page stays visible without JS.
export default function DepthEffects() {
  const { pathname } = useLocation();

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
    let cleanup = () => {};
    const setup = () => {
      cleanup();
      if (preference.matches) return;

      const panels = [...document.querySelectorAll(
        '.benefits, .booking-card, .contact-form, .contact-bottom, .callout, .help-section > div, .stay-guide-grid > div, .contact-access-grid > div, .rate-controls, .price-note'
      )];
      panels.forEach(panel => panel.classList.add('depth-panel'));

      const images = [...document.querySelectorAll(
        '.hero > .sea-picture, .sea-banner > .sea-picture, .island-tilt > .sea-picture, .contact-info > .sea-picture, .help-section > .sea-picture, .card-photo > .gite-picture, .photo-mosaic button > .gite-picture'
      )];
      let frame = 0;
      const update = () => {
        frame = 0;
        images.forEach(picture => {
          const rect = picture.getBoundingClientRect();
          if (!rect.height || rect.bottom < 0 || rect.top > window.innerHeight) return;
          const progress = Math.max(-1, Math.min(1,
            (window.innerHeight / 2 - rect.top - rect.height / 2) / ((window.innerHeight + rect.height) / 2)
          ));
          // Keep enough overscan for every image size, including mobile cards.
          const amplitude = Math.min(56, rect.height * 0.085);
          picture.style.setProperty('--photo-shift', `${(progress * amplitude).toFixed(2)}px`);
        });
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(update);
      };
      images.forEach(picture => picture.classList.add('depth-photo'));
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule);
      update();

      const sections = [...document.querySelectorAll(
        '.section-heading, .page-intro, .gite-grid, .stay-guide-grid > div, .island-copy, .property-description > section, .location-section, .callout, .help-section, .contact-access-grid > div, .contact-form, .contact-bottom, .booking-card, .price-note'
      )];
      const observer = typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('depth-enter');
            observer.unobserve(entry.target);
          });
        }, { threshold: 0.12 })
        : null;
      // Only reveal sections initially below the fold. Never hide page content.
      sections.forEach(section => {
        if (section.getBoundingClientRect().top >= window.innerHeight) observer?.observe(section);
      });

      cleanup = () => {
        if (frame) cancelAnimationFrame(frame);
        window.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        observer?.disconnect();
        panels.forEach(panel => panel.classList.remove('depth-panel'));
        images.forEach(picture => {
          picture.classList.remove('depth-photo');
          picture.style.removeProperty('--photo-shift');
        });
        sections.forEach(section => section.classList.remove('depth-enter'));
      };
    };
    setup();
    preference.addEventListener('change', setup);
    return () => {
      preference.removeEventListener('change', setup);
      cleanup();
    };
  }, [pathname]);

  return null;
}
